import React, { FormEvent, useState } from "react";
import { usePageTitle } from "../../Hooks/usePageTitle";
import "./Login.css";
import { LoginPageProps } from "./LoginProps";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CredentialLoadingScreen } from "../../Components/LoadingScreen/CredentialLoadingScreen";
import { auth } from "../../Backend/firebase";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { applyGrantIfExists } from "../../Services/PlanService/grantCheck";
import { claimMyInvite } from "../../Services/TeamService/teamService";
import { User as FirebaseUser } from "firebase/auth";
import { User } from "../../Interfaces/User/User";
import { safeRedirect } from "../../utils/safeRedirect";

function mapFirebaseUserToAppUser(firebaseUser: FirebaseUser): User {
  return {
    id: firebaseUser.uid,
    email: firebaseUser.email || "",
    firstName: "",
    lastName: "",
    password: "",
  };
}

function Login({ setUser }: LoginPageProps): React.JSX.Element {
  usePageTitle("Log In");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [resetSending, setResetSending] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  async function handleForgotPassword() {
    setResetMsg("");
    setPasswordError("");
    if (!email) {
      setEmailError("Enter your email above, then tap “Forgot password?”");
      return;
    }
    setResetSending(true);
    try {
      // Prefer the branded email (Cloud Function). If it isn't deployed/configured
      // yet, fall back to Firebase's built-in reset email so this always works.
      try {
        await httpsCallable(getFunctions(), "sendPasswordResetBranded")({ email });
      } catch {
        await sendPasswordResetEmail(auth, email);
      }
      // Neutral wording avoids leaking whether an account exists.
      setResetMsg(`If an account exists for ${email}, a reset link is on its way. Check your inbox and spam folder.`);
    } catch (error: any) {
      if (error.code === "auth/invalid-email") {
        setEmailError("That doesn't look like a valid email");
      } else {
        setResetMsg(`If an account exists for ${email}, a reset link is on its way. Check your inbox and spam folder.`);
      }
    } finally {
      setResetSending(false);
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setEmailError("");
    setPasswordError("");

    if (!email) { setEmailError("Email is required"); return; }
    if (!password) { setPasswordError("Password is required"); return; }

    setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      await applyGrantIfExists(credential.user.uid, credential.user.email ?? email);
      // Auto-join a team if this email was invited
      await claimMyInvite();
      const appUser = mapFirebaseUserToAppUser(credential.user);
      sessionStorage.setItem("CURRENT_USER", JSON.stringify(appUser));
      setUser(appUser);
      navigate(safeRedirect(searchParams.get("redirect")));
    } catch (error: any) {
      setLoading(false);
      const code = error.code;
      // Firebase returns auth/invalid-credential for both a wrong password and a
      // non-existent account (email-enumeration protection), so use one neutral
      // message rather than revealing which field was wrong.
      if (
        code === "auth/invalid-credential" ||
        code === "auth/wrong-password" ||
        code === "auth/user-not-found"
      ) {
        setPasswordError("Incorrect email or password");
      } else if (code === "auth/too-many-requests") {
        setPasswordError("Too many attempts. Try again later or reset your password.");
      } else {
        setEmailError(error.message);
      }
    }
  }

  if (loading) return <CredentialLoadingScreen />;

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-logo"><span className="auth-logo-live">LIVE</span><span className="auth-logo-cue">CUE</span></div>
        <h1 className="auth-heading">Welcome back</h1>
        <p className="auth-sub">Sign in to your account</p>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="auth-field">
            <label className="auth-label" htmlFor="login-email">Email</label>
            <input
              id="login-email"
              className={`auth-input${emailError ? ' auth-input--error' : ''}`}
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => { setEmail(e.target.value); setEmailError(""); }}
              autoComplete="email"
            />
            {emailError && <span className="auth-error">{emailError}</span>}
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="login-password">Password</label>
            <input
              id="login-password"
              className={`auth-input${passwordError ? ' auth-input--error' : ''}`}
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={e => { setPassword(e.target.value); setPasswordError(""); }}
              autoComplete="current-password"
            />
            {passwordError && <span className="auth-error">{passwordError}</span>}
            <button
              type="button"
              className="auth-forgot"
              onClick={handleForgotPassword}
              disabled={resetSending}
            >
              {resetSending ? "Sending…" : "Forgot password?"}
            </button>
          </div>

          {resetMsg && <div className="auth-notice">{resetMsg}</div>}

          <button className="auth-submit" type="submit">Sign in</button>
        </form>

        <p className="auth-footer">
          Don't have an account?{' '}
          <Link
            to={
              searchParams.get("redirect")
                ? `/signup?redirect=${encodeURIComponent(searchParams.get("redirect")!)}`
                : searchParams.get("plan")
                  ? `/signup?plan=${searchParams.get("plan")}`
                  : "/signup"
            }
            className="auth-link"
          >Sign up</Link>
        </p>
      </div>
    </div>
  );
}

export default Login;
